import type * as React from 'react';
import { cn } from '../lib/cn';

/**
 * The MyHome+ logo.
 *
 * The house outline is traced from docs/design/assets/logo.png: it is an open
 * outline with no left wall, which is why the wordmark sits across the gap.
 * The wordmark and tagline are set live in Poppins, the brand face (D16), at
 * the measured size and tracking of the original.
 *
 * The repo has no vector logo, only a 400x200 PNG. When Demi supplies the
 * Canva SVG export, replace the paths here with it (DESIGN.md: the logo is
 * rendered from source artwork, never redrawn).
 *
 * On light backgrounds the logo sits on an indigo tile, as in the brand file,
 * which is what `tile` does.
 */
const HOUSE_PATH = 'M217 82 L264.5 44.5 L312 82 L312 139 L216 139';

export interface LogoProps extends Omit<React.ComponentProps<'svg'>, 'viewBox' | 'children'> {
  /**
   * `lockup` is house + wordmark + tagline, for a hero or a letterhead.
   * `wordmark` drops the tagline, which is unreadable below about 60px, and
   * is what app bars use. `mark` is the house alone, for icons and avatars.
   */
  variant?: 'lockup' | 'wordmark' | 'mark';
  /** Draw the indigo tile behind it. Off when the surface is already indigo. */
  tile?: boolean;
  /** Rendered height in pixels. */
  height?: number;
}

function Logo({ variant = 'lockup', tile = true, height = 40, className, ...props }: LogoProps) {
  const isMark = variant === 'mark';
  // The mark crops to the house; the wordmark crops the tagline off the bottom.
  const viewBox = isMark ? '196 24 136 136' : variant === 'wordmark' ? '0 0 400 150' : '0 0 400 200';
  const ratio = isMark ? 1 : variant === 'wordmark' ? 400 / 150 : 400 / 200;
  const width = height * ratio;

  return (
    <svg
      viewBox={viewBox}
      height={height}
      width={width}
      role="img"
      aria-label="MyHome+. More for your home."
      className={cn('shrink-0', className)}
      {...props}
    >
      {tile ? <rect x="-50" y="-50" width="500" height="300" fill="#10074d" /> : null}
      <path
        d={HOUSE_PATH}
        fill="none"
        stroke="#e5aa31"
        strokeWidth="5"
        strokeLinejoin="miter"
        strokeLinecap="butt"
        strokeMiterlimit="10"
      />
      {isMark ? null : (
        <>
          <text
            x="89"
            y="121.8"
            fill="#ffffff"
            fontFamily="var(--font-poppins, Poppins), sans-serif"
            fontWeight="700"
            fontSize="38.32"
          >
            MyHome+
          </text>
          {variant === 'wordmark' ? null : (
            <text
              x="197.5"
              y="158"
              fill="#e5aa31"
              textAnchor="middle"
              fontFamily="var(--font-poppins, Poppins), sans-serif"
              fontWeight="400"
              fontSize="12"
              letterSpacing="1.158"
            >
              MORE FOR YOUR HOME.
            </text>
          )}
        </>
      )}
    </svg>
  );
}

export { HOUSE_PATH, Logo };
